/**
 * Crypto Utilities
 * 
 * Provides secure encryption and decryption functions using the Web Crypto API
 * for protecting sensitive data stored on the client side.
 * 
 * Used primarily for PUID (Pseudonymous User ID) encryption in localStorage
 * to comply with privacy best practices and regulations.
 */

// Encryption key management
const ENCRYPTION_KEY_NAME = 'meduler_crypto_key';
const SALT_NAME = 'meduler_crypto_salt';
const IV_LENGTH = 12; // For GCM mode, 12 bytes is recommended

/**
 * Generates or retrieves a cryptographic key for encryption/decryption
 * Creates a key if none exists, otherwise retrieves from localStorage
 */
async function getCryptoKey(): Promise<CryptoKey> {
  // Check if we already have a key in localStorage
  const storedKey = localStorage.getItem(ENCRYPTION_KEY_NAME);
  
  if (storedKey) {
    try {
      // Convert stored base64 key to ArrayBuffer
      const keyData = base64ToArrayBuffer(storedKey);
      
      // Import the key for use with AES-GCM
      return await window.crypto.subtle.importKey(
        'raw',
        keyData,
        { name: 'AES-GCM', length: 256 },
        false, // not extractable
        ['encrypt', 'decrypt']
      );
    } catch (error) {
      
      // Fall through to key generation if import fails
    }
  }
  
  // Generate a new random key if none exists or import failed
  try {
    const key = await window.crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      true, // extractable
      ['encrypt', 'decrypt']
    );
    
    // Export and store the key for future use
    const exportedKey = await window.crypto.subtle.exportKey('raw', key);
    const keyBase64 = arrayBufferToBase64(exportedKey);
    localStorage.setItem(ENCRYPTION_KEY_NAME, keyBase64);
    
    return key;
  } catch (error) {
    
    throw new Error('Failed to generate encryption key');
  }
}

/**
 * Get or generate a salt for encryption
 */
function getSalt(): Uint8Array {
  const storedSalt = localStorage.getItem(SALT_NAME);
  
  if (storedSalt) {
    try {
      return base64ToArrayBuffer(storedSalt);
    } catch (error) {
      
    }
  }
  
  // Generate a new random salt
  const salt = window.crypto.getRandomValues(new Uint8Array(16));
  localStorage.setItem(SALT_NAME, arrayBufferToBase64(salt));
  return salt;
}

/**
 * Encrypts a string using AES-GCM
 * 
 * @param data The string to encrypt
 * @returns A base64-encoded string containing the IV and ciphertext
 */
export async function encryptData(data: string): Promise<string> {
  try {
    // Get the encryption key
    const key = await getCryptoKey();
    
    // Generate a random initialization vector (IV)
    const iv = window.crypto.getRandomValues(new Uint8Array(IV_LENGTH));
    
    // Convert the input string to an ArrayBuffer
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    
    // Encrypt the data
    const ciphertext = await window.crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv,
        additionalData: getSalt()
      },
      key,
      dataBuffer
    );
    
    // Combine the IV and ciphertext into a single buffer
    const result = new Uint8Array(iv.length + new Uint8Array(ciphertext).length);
    result.set(iv, 0);
    result.set(new Uint8Array(ciphertext), iv.length);
    
    // Convert to base64 for storage
    return arrayBufferToBase64(result);
  } catch (error) {
    
    throw new Error('Failed to encrypt data');
  }
}

/**
 * Decrypts a previously encrypted string
 * 
 * @param encryptedData The base64-encoded string containing IV and ciphertext
 * @returns The decrypted string
 */
export async function decryptData(encryptedData: string): Promise<string> {
  try {
    // Get the encryption key
    const key = await getCryptoKey();
    
    // Convert the base64 input to an ArrayBuffer
    const data = base64ToArrayBuffer(encryptedData);
    
    // Extract the IV from the beginning of the data
    const iv = data.slice(0, IV_LENGTH);
    
    // The rest is the ciphertext
    const ciphertext = data.slice(IV_LENGTH);
    
    // Decrypt the data
    const decryptedBuffer = await window.crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: iv,
        additionalData: getSalt()
      },
      key,
      ciphertext
    );
    
    // Convert the decrypted ArrayBuffer back to a string
    const decoder = new TextDecoder();
    return decoder.decode(decryptedBuffer);
  } catch (error) {
    
    throw new Error('Failed to decrypt data');
  }
}

/**
 * Convert an ArrayBuffer to a base64 string
 */
function arrayBufferToBase64(buffer: ArrayBuffer | Uint8Array): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Convert a base64 string to an ArrayBuffer
 */
function base64ToArrayBuffer(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

/**
 * Check if the Web Crypto API is available in this browser
 * @returns true if crypto is supported
 */
export function isCryptoSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.crypto !== undefined &&
    window.crypto.subtle !== undefined
  );
}
